import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CorporateContactType } from "@/lib/validations/schema";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosRequestConfig } from "axios";
import { company_contact_status } from "common/filters";
import { Spinner } from "components";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { DataTable } from "components/Table/DataTable";
import { Search, X } from "lucide-react";
import NewCustomersPage from "pages/Customers/newcustomer";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getCompanyCount } from "service/api";
// Same signatures, answered by the new backend. See legacy-lookup-api.ts.
import {
  getCompanyLeads,
  GetCompanyRequestType,
} from "service/moneeflo/legacy-lookup-api";
import { Toaster, toast } from "sonner";
import { IPage } from "./partner_contact";
import DailyRatesDialog from "./DailyRatesDialog";
import { stageTags } from "pages/Opportunities/opportunitydetails";
import GenericFilter from "components/Filters/GenericFilter";
import { FilterConfig } from "hooks/useGenericFilter";
import { DialogTitle } from "@radix-ui/react-dialog";
import Select from "react-select";
import { useSession } from "session";
import { ApplicationsTable } from "pages/Applications/ApplicationsTable";
import { useApplications } from "pages/Applications/useApplications";
import {
  APPLICATION_STATUS_FILTER,
  APPLICATION_STATUS_LABELS,
  type ApplicationStatus,
} from "service/business";

const allcolumns: ColumnDef<CorporateContactType>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => {
      const stageTag = stageTags.find(
              (stage) => stage.value.toLowerCase() === row?.original?.stage_tags?.toLowerCase()
            );
      return (
        <div className="space-y-1.5">
          <h2 className="">
            {row.getValue("id")}
          </h2>
          {row?.original?.stage_tags && (
            <div className="flex gap-1.5 items-center">
              <span
                dangerouslySetInnerHTML={{ __html: stageTag?.icon || "" }}
                className=""
              />
              <h4
                className={`${stageTag?.color} text-sm leading-4 !bg-transparent font-semibold`}
              >
                {row.original?.stage_tags}
              </h4>
            </div>
          )}
        </div>
      )
    },
    enableHiding: false,
    enableSorting: false,
    filterFn: "includesString",
  },
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Company Name" />
    ),
    filterFn: "includesString",
    enableSorting: false,
  },
  {
    id: "type",
    accessorKey: "type",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Type"
      />
    ),
    cell: ({ row }) => {
      return <p className=" text-center w-full">{row.getValue("type")}</p>;
    },
    enableSorting: false,
  },
  {
    id: "location",
    accessorKey: "location",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Location"
      />
    ),
    cell: ({ row }) => {
      return (
        <p className=" text-center w-full">
          {row.getValue("location") || "N/A"},{row.original.state || "N/A"}
        </p>
      );
    },
    enableSorting: false,
  },
  {
    id: "assignedTo",
    accessorKey: "assignedTo",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Assigned To"
      />
    ),
    cell: ({ row }) => {
      return (
        <p className=" text-center w-full">{row.getValue("assignedTo")}</p>
      );
    },
    enableSorting: false,
  },
  {
    id: "lead_status",
    accessorKey: "lead_status",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-center"
        column={column}
        title="Status"
      />
    ),
    cell: ({ row }) => {
      const partnerStatus = row.original.status;
      const leadStatus = row.getValue("lead_status");

      const statusToUse =
        partnerStatus === "Active"
          ? partnerStatus
          : leadStatus || partnerStatus;

      const status = company_contact_status.find(
        (status) => status.value === statusToUse
      );

      if (!status) {
        return null;
      }

      return (
        <p
          style={{ backgroundColor: status.bgColor, color: status.textColor }}
          className="py-[3px] text-sm leading-4 text-center font-medium max-w-[160px]  px-2 mx-auto rounded-md"
        >
          {status.label}
        </p>
      );
    },
    enableSorting: false,
  },
];

const tabData = [
  { label: "All", value: "all", enabled: true },
  { label: "Leads", value: "leads", enabled: true },
  { label: "Clients", value: "clients", enabled: true },
];

const APPLICATIONS_TAB = { label: "Applications", value: "applications", enabled: true };

const applicationsFilterConfig: FilterConfig = {
  status: {
    label: "By Status",
    options: APPLICATION_STATUS_FILTER.map((s) => ({
      label: APPLICATION_STATUS_LABELS[s],
      value: s,
    })),
  },
};

const registerBusinessButton =
  "rounded-lg py-[11px] px-4 border bg-blue-700 text-white-A700 font-semibold text-sm cursor-pointer hover:bg-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-700";

const opportunitiesFilterConfig: FilterConfig = {
  status: {
    label: "By Priority",
    options: [
      { label: "Warm", value: "Warm" },
      { label: "Cold", value: "Cold" },
      { label: "Hot", value: "Hot" },
    ],
  },  
};

export default function CorporateContact() {
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState<CorporateContactType[]>([]);
  const { can } = useSession();
  const mayRegister = can("business.create");
  const showApplications = mayRegister || can("business.review");
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() =>
    new URLSearchParams(location.search).get("tab") === "applications"
      ? "applications"
      : "all",
  );
  const applications = useApplications(showApplications);
  const applicationsCount = applications.isError
    ? "–"
    : applications.data
      ? String(applications.data.length)
      : "–";
  const [statusPick, setStatusPick] = useState<ApplicationStatus[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSendingRates, setIsSendingRates] = useState<Boolean>(false);
  const [pageDetails, setPageDetails] = useState<IPage>();
  const navigate = useNavigate();
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);

  const handleFilterChange = (filters: Record<string, string[]>) => {
    if (activeTab === "applications") {
      setStatusPick((filters.status || []) as ApplicationStatus[]);
      return;
    }
    setPriorityFilter(filters.status || []);
  };

  const onRowClick = (row: any) => {
    // Always open the row. This used to be gated on the status being
    // exactly "lead", and the migration had written "active" to every
    // corporate record, so clicking anything did nothing at all — no
    // navigation, no error, no clue. The partner list has the same
    // guard but an else branch to fall through to; this one did not.
    if (!row?.id) return;
    navigate(`/leads/corporate/${row.id}`);
  };

  const columns = useMemo(() => {
    return allcolumns.filter((column) => {
      if (activeTab === "all") {
        return !["contactStatus"].includes(column?.id as string);
      }
      return allcolumns;
    });
  }, [activeTab]);

  const fetchResults = (
    page: number,
    per_page: number,
    searchQuery: string,
    category: string,
    filters: string[] = [] // Add filters parameter
  ) => {
    setLoading(true);

    // get auth token from localstorage
    const auth: string | null = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);

    const payload: AxiosRequestConfig<GetCompanyRequestType> = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      params: {
        category: category,
        q: searchQuery,
        paging: {
          page: page,
          per_page: per_page,
          offset: 0,
        },
        // Add filter parameters to the API call
        ...(filters.length > 0 && { priority_filters: filters }), // Adjust this based on your API
      },
    };

    console.log('API Payload:', payload); // Debug log

    getCompanyLeads(payload)
      .then((response) => {
        console.log(response);
        setLoading(false);
        setCompany(() => {
          let filteredData = response?.data?.map(
            (
              result: Partial<{
                id: number;
                company_name: string;
                address: string | null;
                email: string;
                phone: string | null;
                contacts_id: number[];
                rm_id: number;
                status: string;
                type: string;
                lead_status: string;
                state: string;
                extra_detail: {
                  channel_of_interaction_id: number;
                  service: string[];
                  meeting_staff: string[];
                  _channel_of_interaction: string[];
                };
                rm_staff: {
                  id: number;
                  fname: string;
                };
                coorporate_company_id: number;
                stage_tags: string;
              }>
            ) => {
              // Find the last address object with a valid City or State
              const validAddress = result?.address_id?.slice().reverse().find(
                      (addr: { City: any; State: any; }) => addr?.City || addr?.State
              );
              return {
                id: result?.id,
                name: result?.entity_name,
                location: validAddress?.City || "N/A",
                state: validAddress?.State || "N/A",
                assignedTo: result?.relation_manager?.fname || "N/A",
                type: result?.type || "N/A",
                partnerContactStatus: result?.status || "N/A",
                status: result?.status || "N/A",
                lead_status: result?.lead_status || "N/A",
                staffName: "",
                subType: "",
                coorporate_company_id: result?.coorporate_company_id,
                stage_tags: result?.stage_tags || "",
              };
            }
          ) ?? [];

          // Apply client-side filtering if API doesn't support it
          if (filters.length > 0) {
            filteredData = filteredData.filter((item:any) => {
              // Adjust this logic based on what field you want to filter
              return filters.includes(item.lead_status) || 
                     filters.includes(item.status) ||
                     filters.includes(item.stage_tags);
            });
          }

          return filteredData;
        });
        setPageDetails({
          pageTotal: response?.data?.pageTotal,
          curPage: response?.data?.curPage,
          itemsReceived: response?.data?.itemsReceived,
          itemsTotal: response?.data?.itemsTotal,
          nextPage: response?.data?.nextPage,
          offset: response?.data?.offset,
          perPage: response?.data?.perPage,
          prevPage: response?.data?.prevPage,
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // Updated useEffect to include priorityFilter
  useEffect(() => {
    if (activeTab === "applications") return;
    fetchResults(page, perPage, searchQuery, activeTab, priorityFilter);
  }, [statusFilter, activeTab, page, perPage, searchQuery, priorityFilter]); // Add priorityFilter dependency

  return (
    <>
      {loading && <Spinner />}
      <div className="flex h-full gap-10 py-5 px-6 sm:px-0 w-full flex-col overflow-hidden">
        <div className="flex gap-5 justify-between w-full items-center">
          <Tabs
            className="bg-navbar-menu-bg w-fit max-w-full overflow-x-auto flex rounded-lg gap-2 sm:gap-0"
            value={activeTab}
            onValueChange={(val) => {
              if (val === "partners") {
                setStatusFilter("active");
              } else {
                setStatusFilter("");
              }
              setActiveTab(val);
            }}
          >
            <TabsList className="flex flex-wrap sm:flex-nowrap w-full h-full">
              {[...tabData, ...(showApplications ? [APPLICATIONS_TAB] : [])]
                .filter((tab) => true)
                .map((tab, index: number) => (
                  <TabsTrigger
                    key={index}
                    value={tab.value}
                    disabled={!tab.enabled}
                    className={`py-3 px-10 sm:px-3 disabled:opacity-40 text-sm h-[44px] min-w-[120px] sm:min-w-0 font-semibold ${
                      tab.value === activeTab
                        ? "border border-blue-700 bg-white-A700 text-blue-700 rounded-lg"
                        : " text-black-label"
                    }`}
                  >
                    {tab.label}
                    {tab.value === "applications" && (
                      <span className="ml-1.5 shrink-0 text-xs rounded-full bg-[#E1EBFC] text-blue-700 px-2 py-0.5">
                        {applicationsCount}
                      </span>
                    )}
                  </TabsTrigger>
                ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-start gap-2 w-full max-w-[470px]">
            <div className="relative flex items-center w-full ">
              <Input
                placeholder="Search by corporate name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="py-3 pl-10 pr-4 flex gap-3.5 rounded-[30px] w-full"
              />
              <Search className="absolute left-3 h-4 w-4 text-gray-500" />
            </div>
            <GenericFilter
              key={activeTab === "applications" ? "applications" : "other"}
              config={activeTab === "applications" ? applicationsFilterConfig : opportunitiesFilterConfig}
              onFilterChange={handleFilterChange}
              className="flex gap-2 items-center" // Reduced gap
              filterTriggerClassName="p-2" // Add some padding to the trigger
              activeFilterClassName="" 
            />
          </div>

          {activeTab === "leads" && (
            <button
              className="rounded-lg py-[11px] px-4 border bg-blue-700 text-[#fff] font-semibold text-sm"
              onClick={() => setIsSendingRates(true)}
            >
              Send Daily Rates
            </button>
          )}

          {/* Rest of your dialogs and content remain the same */}
          {mayRegister && (activeTab === "all" || activeTab === "applications") && (
            <Link to="/corporateregistration/business" className="sm:hidden">
              <button type="button" className={registerBusinessButton}>
                Register a business
              </button>
            </Link>
          )}
          {activeTab === "applications" ? null : activeTab === "all" ? (
            <Link to="/lead/createcontact/corporate" className="sm:hidden">
              <button className="rounded-lg py-[11px] px-4 border bg-blue-700 text-white-A700 font-semibold text-sm">
                New Corporate Lead
              </button>
            </Link>
          ) : activeTab !== "leads" ? (
            <Link to="/corporateregistration" className="sm:hidden">
              <button className="rounded-lg py-[11px]  px-4 border bg-blue-700 text-white-A700 font-semibold text-sm">
                Register Corporate
              </button>
            </Link>
          ) : null}

          <DailyRatesDialog
            open={isSendingRates as boolean}
            onOpenChange={(v) => setIsSendingRates(v)}
          />
        </div>

        <div className={`flex-1 relative overflow-auto ${activeTab === "applications" ? "visible-scroll" : ""}`}>
          {loading && <Spinner />}
          {!activeTab ? (
            <NewCustomersPage />
          ) : activeTab === "applications" ? (
            <ApplicationsTable
              rows={applications.data}
              loading={applications.isLoading}
              error={applications.isError}
              onRetry={() => applications.refetch()}
              search={searchQuery}
              statuses={statusPick}
            />
          ) : (
            <DataTable
              columns={columns}
              data={company}
              onRowClick={(row) => onRowClick(row)}
              isToolbarVisible={false} // Fixed duplicate prop
              isPaginationVisible={true}
              isPageCountVisible={false}
              defaultSorting={[{ id: "id", desc: true }]}
              onClickNextPage={() => setPage((page) => page + 1)}
              onClickPreviousPage={() => setPage((page) => page - 1)}
              totalPages={pageDetails?.pageTotal}
              currentPage={pageDetails?.curPage}
              canNextPage={!!pageDetails?.nextPage}
              canPreviousPage={!!pageDetails?.prevPage}
            />
          )}
        </div>

        {/* Rest of your component remains the same */}
        {mayRegister && (activeTab === "all" || activeTab === "applications") && (
          <Link
            className="p-2.5 rounded-[20px] hidden sm:flex text-center border border-blue-700"
            to={"/corporateregistration/business"}
          >
            <button type="button" className={`${registerBusinessButton} w-full`}>
              Register a business
            </button>
          </Link>
        )}
        {activeTab === "applications" ? null : activeTab === "all" ? (
          <Link
            className="p-2.5 rounded-[20px] hidden sm:flex text-center border border-blue-700"
            to={"/lead/createcontact"}
          >
            <button className="rounded-lg py-[11px] px-4 border w-full bg-blue-700 text-white-A700 font-semibold text-sm">
              New Corporate Lead
            </button>
          </Link>
        ) : (
          <Link
            className="p-2.5 rounded-[20px] hidden sm:flex text-center border border-blue-700"
            to={"/corporateregistration"}
          >
            <button className="rounded-lg py-[11px] px-4 border w-full bg-blue-700 text-white-A700 font-semibold text-sm">
              Register Corporate
            </button>
          </Link>
        )}
        <Toaster position="top-right" />
      </div>
    </>
  );
}